<!--
  The Review view's "Private notes on this pull request" (docs/design.md
  "Notes", Promotion): the viewer's notes on the pull request's files, as
  the whole pull request (base → head) shows them, each with what can come
  of it here. A note whose lines are in the head's version of its file is
  offered Promote to draft (state/promotion.ts: a line draft where those
  lines take a line comment, else a file draft naming them); a promoted
  one leads to its draft, or says it was published; and a note none of
  whose lines are at the head, or on a file the pull request deletes, is
  listed with why it can't be promoted.

  A note names content, so the notes are looked up as a Files view looks
  them up: on each file's blobs along the pull request's track, under
  every name the file had there (state/trackBlobs.ts
  `blobsOnTrackPerPath`, all files at once), with the blobs each side shows
  and their other IDs (`DiffModel.aliasOf`). Only files holding notes have
  their diff read, for the lines that take a line comment and the line maps
  that carry a note from an earlier version. Nothing is shown until there
  is a note.
-->
<script lang="ts">
  import type { Note } from '../api/server';
  import { linesText, promotionPlan, waitingOn, type PromotionFile, type PromotionPlan } from '../logic/promotion';
  import { commentableRuns } from '../logic/suggestEdit';
  import type { DiffModel, FileEntry } from '../state/diffs.svelte';
  import { trackPoints } from '../state/fileNotes.svelte';
  import type { PrStore } from '../state/pr.svelte';
  import { promote, promotionText } from '../state/promotion';
  import { session } from '../state/session.svelte';
  import { blobsOnTrackPerPath } from '../state/trackBlobs';
  import { untrack } from 'svelte';
  import Html from './Html.svelte';

  interface Props {
    pr: PrStore;
    /** The pull request's own comparison, base → head (the one a review is published on); null until there is one. */
    diff: DiffModel | null;
    /** Looks up each file's blobs on the track (tests replace it). */
    blobs?: typeof blobsOnTrackPerPath;
  }
  let { pr, diff, blobs = blobsOnTrackPerPath }: Props = $props();

  /** The pull request's files (a rebase's noise is no file of it). */
  const files: FileEntry[] = $derived(diff?.status === 'ready' ? diff.entries.filter((e) => !e.noise) : []);
  /** Where the track's history starts: the merge base of the base and the head, once known. */
  let start: string | null = $state(null);
  $effect(() => {
    const d = diff;
    if (!d) return;
    let stale = false;
    void d.mergeBase().then(
      (sha) => {
        if (!stale) start = sha;
      },
      () => {},
    );
    return () => {
      stale = true;
    };
  });
  /** Each file's (path, blob) pairs on the track, under every name it had (with the blobs its sides show, and their other IDs), once found. */
  let found: ReadonlyMap<string, ReadonlySet<string>> | null = $state.raw(null);
  $effect(() => {
    const list = files;
    const d = diff;
    if (!list.length || !d) return;
    const points = trackPoints(pr.track, start);
    const store = pr.notes;
    let stale = false;
    void blobs(
      pr.owner,
      pr.repo,
      points,
      list.map((f) => f.path),
      { at: d.newCommit ?? pr.headRefOid },
    ).then(
      (perPath) => {
        if (stale) return;
        const out = new Map<string, Set<string>>();
        const asked = new Map<string, Set<string>>();
        for (const f of list) {
          const pairs = new Set<string>();
          const add = (path: string, blob: string | null) => {
            for (const b of blob ? [blob, d.aliasOf(blob)] : [])
              if (b) {
                pairs.add(`${path}\0${b}`);
                (asked.get(path) ?? asked.set(path, new Set()).get(path)!).add(b);
              }
          };
          for (const [name, bs] of perPath.get(f.path)?.blobs ?? []) for (const b of bs) add(name, b);
          add(f.previous ?? f.path, f.oldSha);
          add(f.path, f.newSha);
          out.set(f.path, pairs);
        }
        found = out;
        void store.want([...asked]);
      },
      () => {},
    );
    return () => {
      stale = true;
    };
  });

  /** The viewer's notes on the pull request's files, each with its file. */
  const mine = $derived.by(() => {
    const at = found;
    if (!at) return [];
    return pr.notes.notes.flatMap((n) => {
      const file = files.find((f) => at.get(f.path)?.has(`${n.path}\0${n.blob}`));
      return file ? [{ note: n, file }] : [];
    });
  });
  // Only files holding notes have their diff read.
  $effect(() => {
    const d = diff;
    const paths = [...new Set(mine.map((m) => m.file.path))];
    if (d) untrack(() => paths.forEach((p) => void d.settled(p).catch(() => {})));
  });

  const place = $derived({ owner: pr.owner, repo: pr.repo, webUrl: session.config?.github_web_url ?? 'https://github.com' });
  /** `entry` as a note is promoted on it, once its diff is read; why not, when it can't be. */
  function promotionFile(entry: FileEntry): PromotionFile | PromotionPlan | null {
    const d = diff;
    const fd = d?.diffs[entry.path];
    const commit = d?.newCommit ?? pr.headRefOid;
    if (!d || !fd || fd.status === 'loading' || !commit) return null;
    if (fd.status !== 'ready' || !fd.sides) return { kind: 'refused', why: 'Its file’s text can’t be read here (binary, too large, or not loaded).' };
    const blob = fd.sides.new?.blob ?? null;
    const alias = blob ? d.aliasOf(blob) : undefined;
    return { path: entry.path, blob, aliases: alias ? [alias] : [], lines: fd.newLines, hunks: commentableRuns(fd.set?.blocks ?? []), commit, inPr: true };
  }
  /** Each note with how it is promoted here (null while its file's diff is read). */
  const rows = $derived(
    mine.map(({ note, file }) => {
      const at = promotionFile(file);
      const plan: PromotionPlan | null = at === null ? null : 'kind' in at ? at : promotionPlan(note, at, diff?.lineMaps ?? new Map(), place);
      return { note, file, plan };
    }),
  );
  $effect(() => {
    const need = waitingOn(rows.flatMap((r) => (r.plan ? [r.plan] : [])));
    const d = diff;
    if (need.length && d) untrack(() => d.wantLineMaps(need));
  });

  const promotable = $derived(rows.filter((r) => !r.note.promotion && r.plan?.kind !== 'refused'));
  const promoted = $derived(rows.filter((r) => r.note.promotion));
  const elsewhere = $derived(rows.filter((r) => !r.note.promotion && r.plan?.kind === 'refused'));
  /** Why no draft can be written here now, else null. */
  const off = $derived(pr.readOnly ? (pr.readOnlyText ?? 'Read-only') : pr.draftsOffText);
  let busy: number | null = $state(null);

  async function promoteRow(n: Note, plan: PromotionPlan | null) {
    if (plan?.kind !== 'ready' || busy !== null) return;
    busy = n.id;
    try {
      await promote({ conversation: pr, notes: pr.notes }, n, plan);
    } finally {
      busy = null;
    }
  }
  const author = (n: Note) => (n.author.kind === 'agent' ? `${n.author.label} (your agent)` : 'You');
  const where = (n: Note, f: FileEntry, plan: PromotionPlan | null) =>
    `${f.path} · ${plan?.kind === 'ready' ? linesText(plan.from, plan.to) : `${n.side === 'old' ? 'old ' : ''}${linesText(n.start_line, n.end_line)} at ${n.commit_sha.slice(0, 7)}`}`;
</script>

{#snippet card(n: Note, f: FileEntry, plan: PromotionPlan | null)}
  <div class="note-head">
    <b>{author(n)}</b>
    <span class="mono note-where">{where(n, f, plan)}</span>
  </div>
  <Html md={n.body} class={n.author.kind === 'agent' ? 'comment-body relay-body' : 'comment-body'} />
{/snippet}

{#if rows.length}
  <section class="review-part review-notes" aria-labelledby="review-notes-title">
    <div class="part-head">
      <h3 id="review-notes-title">Private notes on this pull request <span class="tick-count none">{rows.length}</span></h3>
    </div>
    <p class="part-note">Only you see these. Promote one to make it a draft comment here, unsent like any draft; nothing is posted until you publish.</p>
    <ul class="note-list">
      {#each promotable as { note, file, plan } (note.id)}
        <li class="card note-item" data-note={note.id}>
          {@render card(note, file, plan)}
          <div class="note-actions">
            <button
              class="micro note-promote"
              disabled={plan?.kind !== 'ready' || !!off || busy !== null}
              title={off ??
                (plan?.kind === 'ready'
                  ? `Make it a draft comment on ${linesText(plan.from, plan.to)} of ${file.path}${plan.target.kind === 'file' ? ' (outside the diff, so a file comment naming them)' : ''}`
                  : 'Finding its lines in this version…')}
              onclick={() => promoteRow(note, plan)}>{plan?.kind === 'ready' ? 'Promote to draft' : 'Finding its lines…'}</button
            >
          </div>
        </li>
      {/each}
      {#each promoted as { note, file, plan } (note.id)}
        {@const p = promotionText(note.promotion!, { owner: pr.owner, repo: pr.repo, number: pr.number })}
        <li class="card note-item promoted" data-note={note.id}>
          {@render card(note, file, plan)}
          <div class="note-actions"><a class="note-promoted" href={p.href}>{p.text}</a></div>
        </li>
      {/each}
    </ul>
    {#if elsewhere.length}
      <h4 class="notes-elsewhere-title">Not in this version</h4>
      <p class="part-note">Notes on these files whose lines aren’t at the head: they show in Files where their lines are.</p>
      <ul class="note-list">
        {#each elsewhere as { note, file, plan } (note.id)}
          <li class="card note-item elsewhere" data-note={note.id}>
            {@render card(note, file, plan)}
            <p class="note-why">{plan?.kind === 'refused' ? plan.why : ''}</p>
          </li>
        {/each}
      </ul>
    {/if}
  </section>
{/if}

<style>
  .note-list {
    list-style: none;
    margin: 0;
    padding: 0;
    display: grid;
    gap: 8px;
  }
  .note-item {
    display: grid;
    gap: 4px;
    border-left: 3px solid var(--merged);
  }
  .note-head {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    align-items: baseline;
  }
  .note-where,
  .note-why {
    color: var(--muted);
    font-size: 0.85em;
    margin: 0;
  }
  .note-actions {
    display: flex;
    justify-content: flex-end;
  }
  .notes-elsewhere-title {
    margin: 12px 0 0;
    font-size: 13px;
  }
  .review-notes :global(.relay-body) {
    overflow-wrap: anywhere;
  }
</style>
